import type { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import { styled } from "../../../styled-components";

interface Props {
    readonly maxLines: number;
    readonly lineHeight: number;
    readonly appKind: AppKind;
}
interface State {
    isOpen: boolean;
    needExpander: boolean;
}
const TextExpanderStyle = styled.div<Props & State>`
    max-height: ${p => (p.isOpen ? "unset" : p.maxLines * p.lineHeight + "px")};
    overflow: hidden;

    position: relative;

    & > .te-more-button {
        background-image: ${p => p.theme.baseColor};
        background-image: linear-gradient(to right, ${p => p.theme.baseColor}00, ${p => p.theme.baseColor}ff 20%);

        color: ${p => p.theme.primaryAccentColor};
        padding-left: 16px;

        position: absolute;
        right: 0;
        bottom: 0;

        font-weight: 500;
    }
`;

export default class TextExpander extends React.PureComponent<Props, State> {
    public state: State = { isOpen: false, needExpander: false };

    private wrapperRef = React.createRef<HTMLDivElement>();

    private onOpen = (ev: React.MouseEvent) => {
        if (this.state.isOpen) {
            return;
        }
        this.setState({ isOpen: true });
        ev.stopPropagation();
        ev.preventDefault();
    };

    private onResize = (_width?: number, height?: number) => {
        const current = this.wrapperRef.current;
        if (current === null || this.state.isOpen || height === undefined) return;

        const child = current.children[1];
        if (child === undefined || child === null) return;

        const childHeight = Math.floor(child.getBoundingClientRect().height);

        if (Math.abs(height - childHeight) > 1 && !this.state.needExpander) {
            this.setState({ needExpander: true });
        } else if (childHeight <= height && this.state.needExpander) {
            this.setState({ needExpander: false });
        }
    };

    public render(): React.ReactNode {
        return (
            <TextExpanderStyle
                data-test="glide-expander"
                {...this.props}
                {...this.state}
                ref={this.wrapperRef as any}
                onClick={this.onOpen}>
                <ReactResizeDetector handleHeight={true} onResize={this.onResize} />
                {this.props.children}
                {!this.state.isOpen && this.state.needExpander && (
                    <div className="te-more-button">{getLocalizedString("more", this.props.appKind)}</div>
                )}
            </TextExpanderStyle>
        );
    }
}
